<script setup>
import { $api } from "@/utils/api";
import {
  maskCpfOrCnpj,
  maskCep,
  maskRg,
  maskTelefone,
  onlyDigits,
} from "@/utils/masks";

definePage({
  meta: {
    requiresAuth: true,
  },
});

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id);

const prestador = ref(null);
const loading = ref(true);
const isEditMode = ref(false);
const dialogExcluir = ref(false);
const excluindo = ref(false);
const salvando = ref(false);
const modalSucessoEdicao = ref(false);
const snackbar = ref(false);
const snackbarMsg = ref("");
const snackbarColor = ref("success");

// CPF duplicado na edição (CA2)
const cpfDuplicado = ref(false);
const cpfValidando = ref(false);

// Permissões (integrar com CASL quando existir)
const canEdit = ref(true);
const canDelete = ref(true);

// Cópia para edição (Funcionalidade 05)
const form = ref({});

// Opções para selects (mesmos do cadastro)
const cidadesOptions = ref([]);
const ufsOptions = ref([]);
const funcoesOptions = ref([]);
const tiposTerceiroOptions = ref([]);

const cpfDigits = computed(() => onlyDigits(String(form.value.CPF || "")));
const cpfValido = computed(() => {
  const d = cpfDigits.value;
  return d.length === 0 || d.length === 11 || d.length === 14;
});
const podeConfirmar = computed(() => {
  if (!form.value.NOME?.trim()) return false;
  if (cpfDuplicado.value) return false;
  if (cpfDigits.value.length > 0 && !cpfValido.value) return false;
  return true;
});

function formatCpf(val) {
  if (!val) return "";
  const d = onlyDigits(String(val));
  return d ? maskCpfOrCnpj(d) : val;
}

function formatCep(val) {
  if (!val) return "";
  return maskCep(String(val).replace(/\D/g, ""));
}

function formatFone(val) {
  if (!val) return "";
  return maskTelefone(String(val).replace(/\D/g, ""));
}

async function fetchCidades() {
  try {
    const list = await $api("/cidades");
    cidadesOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    cidadesOptions.value = [];
  }
}
async function fetchEstados() {
  try {
    const list = await $api("/estados");
    ufsOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    ufsOptions.value = [];
  }
}
async function fetchFuncoes() {
  try {
    const list = await $api("/funcoes");
    funcoesOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    funcoesOptions.value = [];
  }
}
async function fetchTiposTerceiro() {
  try {
    const list = await $api("/tipos-terceiro");
    tiposTerceiroOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    tiposTerceiroOptions.value = [];
  }
}

async function fetchPrestador() {
  loading.value = true;
  try {
    prestador.value = await $api(`/prestadores/${id.value}`);
    form.value = { ...prestador.value };
  } catch (e) {
    console.error(e);
    prestador.value = null;
    snackbarMsg.value = "Prestador não encontrado.";
    snackbarColor.value = "error";
    snackbar.value = true;
  } finally {
    loading.value = false;
  }
}

function entrarEdicao() {
  isEditMode.value = true;
  form.value = { ...prestador.value };
  cpfDuplicado.value = false;
}

function cancelarEdicao() {
  isEditMode.value = false;
  form.value = { ...prestador.value };
  cpfDuplicado.value = false;
}

async function verificarCpfDuplicado() {
  const d = cpfDigits.value;
  if (d.length !== 11 && d.length !== 14) {
    cpfDuplicado.value = false;
    return;
  }
  cpfValidando.value = true;
  try {
    const res = await $api(`/prestadores/verificar-cpf?cpf=${d}&excluir_id=${id.value}`);
    cpfDuplicado.value = res?.existe ?? false;
  } catch (e) {
    cpfDuplicado.value = false;
  } finally {
    cpfValidando.value = false;
  }
}

function onCpfBlur() {
  verificarCpfDuplicado();
}

async function confirmarEdicao() {
  if (!podeConfirmar.value) return;
  const payload = {
    NOME: form.value.NOME?.trim() || null,
    APELIDO: form.value.APELIDO || null,
    DATA_NASCIMENTO: form.value.DATA_NASCIMENTO || null,
    DATA_ADMISSAO: form.value.DATA_ADMISSAO || null,
    DATA_DEMISSAO: form.value.DATA_DEMISSAO || null,
    CPF: form.value.CPF ? String(form.value.CPF).replace(/\D/g, "") : null,
    RG: form.value.RG || null,
    N_CARTEIRA: form.value.N_CARTEIRA || null,
    FONE1: form.value.FONE1 || null,
    FONE2: form.value.FONE2 || null,
    CELULAR1: form.value.CELULAR1 || null,
    CELULAR2: form.value.CELULAR2 || null,
    DDD: form.value.DDD || null,
    ENDERECO: form.value.ENDERECO?.trim() || null,
    NUMERO: form.value.NUMERO?.trim() || null,
    COMPLEMENTO: form.value.COMPLEMENTO || null,
    CEP: form.value.CEP || null,
    COD_CIDADE: form.value.COD_CIDADE ?? null,
    COD_UF: form.value.COD_UF ?? null,
    COD_FUNCAO: form.value.COD_FUNCAO ?? null,
    TERCEIRO: form.value.TERCEIRO ?? "N",
    COD_TIPO_TERCEIRO: form.value.COD_TIPO_TERCEIRO ?? null,
    ATIVO: form.value.ATIVO ?? 1,
    EMAIL: form.value.EMAIL || null,
    OBS: form.value.OBS || null,
  };
  if (payload.ENDERECO && !payload.NUMERO) payload.NUMERO = "S/N";

  salvando.value = true;
  try {
    await $api(`/prestadores/${id.value}`, { method: "PUT", body: payload });
    modalSucessoEdicao.value = true;
  } catch (e) {
    snackbarMsg.value = e?.data?.message || "Erro ao salvar alterações.";
    snackbarColor.value = "error";
    snackbar.value = true;
  } finally {
    salvando.value = false;
  }
}

function fecharModalSucessoEdicao() {
  modalSucessoEdicao.value = false;
  isEditMode.value = false;
  fetchPrestador();
}

function updateCpf(val) {
  if (isEditMode.value) form.value.CPF = onlyDigits(val);
}
function updateCep(val) {
  if (isEditMode.value) form.value.CEP = onlyDigits(val);
}
function updateFone1(val) {
  if (isEditMode.value) form.value.FONE1 = onlyDigits(val).slice(0, 11);
}
function updateFone2(val) {
  if (isEditMode.value) form.value.FONE2 = onlyDigits(val).slice(0, 11);
}
function updateCel1(val) {
  if (isEditMode.value) form.value.CELULAR1 = onlyDigits(val).slice(0, 11);
}
function updateCel2(val) {
  if (isEditMode.value) form.value.CELULAR2 = onlyDigits(val).slice(0, 11);
}

function abrirModalExcluir() {
  dialogExcluir.value = true;
}

function fecharModalExcluir() {
  if (!excluindo.value) dialogExcluir.value = false;
}

async function confirmarExcluir() {
  excluindo.value = true;
  try {
    await $api(`/prestadores/${id.value}`, { method: "DELETE" });
    snackbarMsg.value = "Prestador excluído com sucesso.";
    snackbarColor.value = "success";
    snackbar.value = true;
    dialogExcluir.value = false;
    router.push({ name: "prestadores" });
  } catch (e) {
    console.error(e);
    snackbarMsg.value = e?.data?.message || "Erro ao excluir prestador.";
    snackbarColor.value = "error";
    snackbar.value = true;
  } finally {
    excluindo.value = false;
  }
}

onMounted(async () => {
  await Promise.all([
    fetchPrestador(),
    fetchCidades(),
    fetchEstados(),
    fetchFuncoes(),
    fetchTiposTerceiro(),
  ]);
});
</script>

<template>
  <div>
    <VBtn
      class="mb-4"
      variant="text"
      :to="{ name: 'prestadores' }"
    >
      <VIcon icon="tabler-arrow-left" class="me-1" />
      Voltar
    </VBtn>

    <VCard v-if="loading">
      <VCardText class="text-center py-8">
        <VProgressCircular indeterminate color="primary" />
        <p class="mt-2 text-body-2">Carregando...</p>
      </VCardText>
    </VCard>

    <VCard v-else-if="prestador">
      <VCardTitle class="d-flex align-center flex-wrap gap-2 pa-4">
        <span class="text-h5">Detalhes do Prestador</span>
        <VSpacer />
        <template v-if="!isEditMode">
          <VBtn
            v-if="canEdit"
            color="primary"
            variant="tonal"
            prepend-icon="tabler-edit"
            @click="entrarEdicao"
          >
            Editar
          </VBtn>
          <VBtn
            v-if="canDelete"
            color="error"
            variant="tonal"
            prepend-icon="tabler-trash"
            @click="abrirModalExcluir"
          >
            Excluir
          </VBtn>
        </template>
        <template v-else>
          <VBtn
            color="primary"
            prepend-icon="tabler-check"
            :disabled="!podeConfirmar"
            :loading="salvando"
            @click="confirmarEdicao"
          >
            Confirmar
          </VBtn>
          <VBtn
            variant="outlined"
            prepend-icon="tabler-x"
            :disabled="salvando"
            @click="cancelarEdicao"
          >
            Cancelar
          </VBtn>
        </template>
      </VCardTitle>
      <VDivider />
      <VCardText class="pa-4">
        <VRow>
          <!-- Código (somente leitura) -->
          <VCol cols="12" md="4">
            <VTextField
              :model-value="form.id"
              label="Código"
              disabled
              density="compact"
              variant="outlined"
            />
          </VCol>
          <VCol cols="12" md="8">
            <VTextField
              v-model="form.NOME"
              label="Nome"
              required
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="form.DATA_NASCIMENTO"
              label="Data de Nascimento"
              type="date"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              :model-value="!isEditMode ? formatCpf(form.CPF) : form.CPF"
              label="CPF / CNPJ"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
              :error-messages="
                cpfDuplicado
                  ? ['Este CPF/CNPJ já está cadastrado.']
                  : cpfDigits.length > 0 && !cpfValido
                    ? ['Informe 11 (CPF) ou 14 (CNPJ) dígitos.']
                    : []
              "
              :loading="cpfValidando"
              @update:model-value="updateCpf"
              @blur="onCpfBlur"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              :model-value="!isEditMode && form.RG ? maskRg(form.RG) : form.RG"
              label="RG"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
              @update:model-value="(v) => { if (isEditMode.value) form.RG = onlyDigits(v).slice(0, 9) }"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              :model-value="formatFone(form.FONE1)"
              label="Telefone"
              placeholder="(00) 00000-0000"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
              maxlength="15"
              @update:model-value="updateFone1"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="form.ENDERECO"
              label="Endereço"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="form.NUMERO"
              label="Número"
              placeholder="Ou S/N se sem número"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              :model-value="!isEditMode ? formatCep(form.CEP) : form.CEP"
              label="CEP"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
              @update:model-value="updateCep"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect
              v-model="form.COD_CIDADE"
              label="Cidade"
              :items="cidadesOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
              :disabled="!isEditMode"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect
              v-model="form.COD_UF"
              label="UF"
              :items="ufsOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
              :disabled="!isEditMode"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect
              v-model="form.COD_FUNCAO"
              label="Função"
              :items="funcoesOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
              :disabled="!isEditMode"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VRadioGroup
              v-model="form.TERCEIRO"
              label="Terceiro"
              inline
              density="compact"
              :disabled="!isEditMode"
            >
              <VRadio label="Não" value="N" />
              <VRadio label="Sim" value="S" />
            </VRadioGroup>
          </VCol>
          <VCol v-if="form.TERCEIRO === 'S'" cols="12" md="6">
            <VSelect
              v-model="form.COD_TIPO_TERCEIRO"
              label="Tipo de Terceiro"
              :items="tiposTerceiroOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
              :disabled="!isEditMode"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VRadioGroup
              v-model="form.ATIVO"
              label="Status"
              inline
              density="compact"
              :disabled="!isEditMode"
            >
              <VRadio label="Ativo" :value="1" />
              <VRadio label="Inativo" :value="0" />
            </VRadioGroup>
          </VCol>
          <VCol cols="12">
            <VTextarea
              v-model="form.OBS"
              label="Observações"
              :disabled="!isEditMode"
              density="compact"
              variant="outlined"
              rows="3"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Modal confirmar exclusão -->
    <VDialog
      v-model="dialogExcluir"
      max-width="400"
      persistent
    >
      <VCard>
        <VCardTitle class="text-h6">Excluir Prestador</VCardTitle>
        <VCardText>
          Deseja realmente excluir permanentemente esse Prestador?
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="excluindo"
            @click="fecharModalExcluir"
          >
            Não
          </VBtn>
          <VBtn
            color="error"
            :loading="excluindo"
            @click="confirmarExcluir"
          >
            Sim
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Modal sucesso edição (CA4) -->
    <VDialog v-model="modalSucessoEdicao" max-width="400" persistent>
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-circle-check" color="success" class="me-2" />
          Sucesso
          <VSpacer />
          <VBtn icon variant="text" size="small" @click="fecharModalSucessoEdicao">
            <VIcon icon="tabler-x" />
          </VBtn>
        </VCardTitle>
        <VCardText>
          Prestador editado com sucesso!
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn color="primary" variant="tonal" @click="fecharModalSucessoEdicao">
            Fechar
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      location="top"
      :timeout="4000"
    >
      {{ snackbarMsg }}
    </VSnackbar>
  </div>
</template>
